// V2 — Modo oscuro (PR 10, reunión Tecnipak 21-08).
//
// Toggle de tema en el navbar. El estado EXPLICITO del usuario viaja en la
// cookie `tp_theme` (no localStorage): así el layout la lee server-side y
// estampa <html data-theme>, con lo que la primera pintura ya viene en el tema
// elegido — sin flash de tema claro ni script inline en <head>.
//
// Sin cookie (primera visita) no se estampa nada y el CSS decide solito con
// `prefers-color-scheme`; este controller solo sincroniza data-bs-theme para
// que el chrome de Bootstrap (tooltips, modales) acompañe al tema efectivo.
//
// El botón no refleja estado en el icono (sol-luna siempre): el tema visible ES
// el estado, y evitamos sincronizar clases que el morphing de Turbo pueda
// dejar cojas (regla de v2-tables-modals: el estado visible vive en el HTML).
import { Controller } from "@hotwired/stimulus"

const THEME_COOKIE = "tp_theme"
const THEME_YEARS = 60 * 60 * 24 * 365

export default class extends Controller {
  static values = { theme: String } // "light" | "dark" | "" (sin elección)

  connect() {
    this.syncBootstrapTheme()
    // Sin elección explícita el tema lo decide el SO, y el SO puede cambiarlo
    // con la página abierta (apariencia automática al anochecer/amanecer). El
    // CSS lo sigue solo por la media query, pero los gráficos pintan con los
    // tokens leídos al renderizar: sin avisarles se quedaban con la paleta
    // anterior (feedback 2026-09-21, GRA.T4).
    this._scheme = window.matchMedia("(prefers-color-scheme: dark)")
    this._onSchemeChange = () => {
      if (this.themeValue === "light" || this.themeValue === "dark") return

      this.syncBootstrapTheme()
      this.announce(this.effectiveTheme())
    }
    this._scheme.addEventListener("change", this._onSchemeChange)
  }

  disconnect() {
    this._scheme?.removeEventListener("change", this._onSchemeChange)
  }

  toggle() {
    const next = this.effectiveTheme() === "dark" ? "light" : "dark"
    document.documentElement.dataset.theme = next
    document.cookie = `${THEME_COOKIE}=${next};path=/;max-age=${THEME_YEARS};samesite=lax`
    this.themeValue = next
    this.syncBootstrapTheme()
    this.announce(next)
  }

  // Los charts leen los tokens --tp-* UNA vez en connect(): sin este evento
  // conservan la paleta del tema anterior hasta la próxima carga completa
  // (misma convención window-event que `v2--chart:refit`). Se emite DESPUÉS
  // de girar el atributo, así quien escucha lee los tokens del tema nuevo.
  announce(theme) {
    window.dispatchEvent(new CustomEvent("v2--theme:change", { detail: { theme } }))
  }

  // "light"/"dark" explícito o, sin elección, lo que prefiera el SO.
  effectiveTheme() {
    if (this.themeValue === "light" || this.themeValue === "dark") return this.themeValue

    const stamped = document.documentElement.dataset.theme
    if (stamped === "light" || stamped === "dark") return stamped

    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }

  // Bootstrap theme-a su chrome vía data-bs-theme; los colores propios ya los
  // pisan los --bs-* de _semantic.scss (mayor especificidad).
  syncBootstrapTheme() {
    document.documentElement.dataset.bsTheme = this.effectiveTheme()
  }
}
